import { buildBrowserLifecycleAttempt, captureBrowserLifecycleTerminal, FINAL_RENDER_METRIC, } from '#pplx/frontend/apps/ask/o11y/browserLifecycleAttempt.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { entryClient } from '#pplx_generated/typespec/rest/entry/client.js';
const UPLOAD_REASON = 'ask-lifecycle-upload';
const UPLOAD_DELAY_MS = 1_500;
const HTTP_BAD_REQUEST = 400;
/**
 * Browser milestones this change introduces. A tier that predates them rejects
 * the whole upload, so the compatibility resend drops them; everything else in
 * the lane is what such a tier already accepted. Retire this set, and the
 * resend with it, once no deployment predates these milestones.
 */
const ADDED_BROWSER_MILESTONES = new Set([
    'CONTENT_FIRST_ANSWER_CANDIDATE_RECEIVED',
    'RENDER_THREAD_STATE_CHANGED',
    'RENDER_LIVEMARK_STATE_CHANGED',
]);
const PENDING_RENDER_TIMEOUT_MS = 10 * 60 * 1_000;
/** A flush this long after the terminal event is the user opening the thread later, not the answer's own render. */
const FINAL_RENDER_MAX_DELAY_MS = 60 * 1_000;
/** A rendered flush older than this cannot be the one racing the stream's own `completed` event. */
const RENDER_RACE_WINDOW_MS = 5 * 1_000;
/** The upload rejects more than 24 events (`@maxItems` in `typespec/rest/entry/router.tsp`),
 *  and the browser lane already spends 11 of them on milestones. */
const MAX_RENDER_DIAGNOSTICS = 13;
const MAX_DIAGNOSTIC_ADAPTER_COUNT = 64;
const pendingRenders = new Map();
const finalizedStreamIds = new Set();
const MAX_FINALIZED_STREAM_IDS = 256;
const renderedThreadsAt = new Map();
const threadRenderStates = new Map();
const livemarkRenderStates = new Map();
const MAX_RENDERED_THREADS = 32;
let uploadEnabled = false;
function evictOldest(collection, limit) {
    if (collection.size <= limit) {
        return;
    }
    const oldest = collection.keys().next().value;
    if (oldest !== undefined) {
        collection.delete(oldest);
    }
}
function browserVisibilityState() {
    if (typeof document === 'undefined')
        return 'BROWSER_VISIBILITY_UNKNOWN';
    // A speculation-rules prerender reports `visibilityState: 'hidden'`; the old
    // `'prerender'` value was dropped from the spec, so `document.prerendering`
    // is the only signal that separates it from a backgrounded tab.
    if (document.prerendering)
        return 'BROWSER_PRERENDER';
    switch (document.visibilityState) {
        case 'visible':
            return 'BROWSER_VISIBLE';
        case 'hidden':
            return 'BROWSER_HIDDEN';
        default:
            return 'BROWSER_VISIBILITY_UNKNOWN';
    }
}
function renderDiagnosticEvent(stream, milestone, cached) {
    return {
        milestone,
        local_elapsed_ms: Math.max(0, Math.round((cached.observedAtEpochMs - stream.timer.getAbsoluteStartTime()) * 1000) / 1000),
        observed_at_epoch_ms: Math.round(cached.observedAtEpochMs),
        render_diagnostic: cached.renderDiagnostic,
    };
}
function appendRenderDiagnostic(diagnostics, event) {
    if (diagnostics.length < MAX_RENDER_DIAGNOSTICS) {
        diagnostics.push(event);
        return;
    }
    // Full: keep the earliest observations, which show what was already pending
    // at the terminal event, and spend the newest Livemark slot on the readiness
    // that ends the wait.
    if (!event.render_diagnostic?.livemark_aggregate_ready)
        return;
    const replaceIndex = diagnostics.findLastIndex((candidate) => candidate.milestone === 'RENDER_LIVEMARK_STATE_CHANGED');
    if (replaceIndex >= 0)
        diagnostics[replaceIndex] = event;
}
function appendCachedDiagnostic(pending, milestone, cached) {
    appendRenderDiagnostic(pending.renderDiagnostics, renderDiagnosticEvent(pending.stream, milestone, cached));
}
function pendingRendersForThread(threadId) {
    return [...pendingRenders.values()].filter((pending) => pending.threadId === threadId);
}
function finalize(stream, threadId, outcome, terminal, rendered, renderDiagnostics = []) {
    if (rendered) {
        stream.timer.addTimingOnce(FINAL_RENDER_METRIC);
    }
    void uploadAttempt(stream, threadId, outcome, terminal, renderDiagnostics);
}
export function setAskLifecycleUploadEnabled(enabled) {
    uploadEnabled = enabled;
}
const FAILED_TERMINAL_STATUSES = new Set(['FAILED', 'BLOCKED']);
/**
 * Maps a terminal stream event to the lane outcome. An end-of-stream whose
 * payload reports anything but `COMPLETED` is a failed attempt, matching how
 * the REST lane classifies its last payload. On the error path only a payload
 * that already reached a failed terminal status (`FAILED`, `BLOCKED`) counts as
 * `FAILED`; an entry still in flight (`PENDING`, `RESUMING`, …) or no payload
 * at all means the transport broke.
 */
export function resolveAskLifecycleOutcome(kind, message) {
    const status = message?.status;
    switch (kind) {
        case 'completed':
            return status === undefined || status === 'COMPLETED' ? 'COMPLETED' : 'FAILED';
        case 'aborted':
            return 'ABORTED';
        case 'error':
            return status !== undefined && FAILED_TERMINAL_STATUSES.has(status)
                ? 'FAILED'
                : 'TRANSPORT_ERROR';
    }
}
/**
 * Records that a live stream reached a terminal state; only the first terminal
 * event per stream counts. Failed and aborted attempts upload immediately and
 * never get a final-render milestone; completed attempts wait for the thread
 * to report a committed render — unless that thread reported one moments ago
 * with no attempt waiting (the flush can win the race against the stream's own
 * `completed` event), in which case the attempt finalizes right away and
 * consumes that report. Temporary disconnects are not terminal and must not be
 * reported here.
 *
 * A stream learns its `threadId` from its first message, so a reconnect that
 * fails before one arrives has an `entryId` but no thread; `entryContextUuid`
 * is that entry's stored context and stands in for the missing thread id.
 */
export function markAskLifecycleTerminal(stream, outcome, entryContextUuid) {
    const streamId = stream.id.description;
    if (finalizedStreamIds.has(streamId)) {
        return;
    }
    finalizedStreamIds.add(streamId);
    evictOldest(finalizedStreamIds, MAX_FINALIZED_STREAM_IDS);
    if (!stream.entryId) {
        return;
    }
    const threadId = stream.threadId ?? entryContextUuid ?? undefined;
    const terminal = captureBrowserLifecycleTerminal(stream);
    if (outcome !== 'COMPLETED') {
        finalize(stream, threadId, outcome, terminal, false);
        return;
    }
    const now = Date.now();
    const renderDiagnostics = [];
    const cachedThreadState = threadId ? threadRenderStates.get(threadId) : undefined;
    if (cachedThreadState && now - cachedThreadState.observedAtEpochMs <= RENDER_RACE_WINDOW_MS) {
        appendRenderDiagnostic(renderDiagnostics, renderDiagnosticEvent(stream, 'RENDER_THREAD_STATE_CHANGED', cachedThreadState));
    }
    const cachedLivemarkState = threadId ? livemarkRenderStates.get(threadId) : undefined;
    if (cachedLivemarkState && now - cachedLivemarkState.observedAtEpochMs <= RENDER_RACE_WINDOW_MS) {
        appendRenderDiagnostic(renderDiagnostics, renderDiagnosticEvent(stream, 'RENDER_LIVEMARK_STATE_CHANGED', cachedLivemarkState));
    }
    const renderedAt = threadId === undefined ? undefined : renderedThreadsAt.get(threadId);
    if (threadId !== undefined && renderedAt !== undefined) {
        renderedThreadsAt.delete(threadId);
        if (now - renderedAt <= RENDER_RACE_WINDOW_MS) {
            finalize(stream, threadId, outcome, terminal, true, renderDiagnostics);
            return;
        }
    }
    const timeoutId = setTimeout(() => {
        pendingRenders.delete(streamId);
        finalize(stream, threadId, outcome, terminal, false, renderDiagnostics);
    }, PENDING_RENDER_TIMEOUT_MS);
    const pending = {
        stream,
        threadId,
        outcome,
        terminal,
        terminalAt: now,
        renderDiagnostics,
        timeoutId,
    };
    pendingRenders.set(streamId, pending);
}
/**
 * Thread-level render prerequisites, cached per thread and attached to any
 * attempt of that thread still waiting for its render. Nothing reads these
 * unless the attempt uploads, so an attempt that cannot upload records nothing.
 */
export function notifyAskLifecycleThreadRenderState(threadId, state) {
    if (!uploadEnabled || !threadId)
        return;
    const cached = {
        observedAtEpochMs: Date.now(),
        renderDiagnostic: {
            visibility_state: browserVisibilityState(),
            foreground_idle: state.foregroundIdle,
            background_idle: state.backgroundIdle,
            has_result: state.hasResult,
            terminal_non_reconnectable: state.terminalNonReconnectable,
        },
    };
    threadRenderStates.set(threadId, cached);
    evictOldest(threadRenderStates, MAX_RENDERED_THREADS);
    for (const pending of pendingRendersForThread(threadId)) {
        appendCachedDiagnostic(pending, 'RENDER_THREAD_STATE_CHANGED', cached);
    }
}
export function notifyAskLifecycleLivemarkReadiness(threadId, snapshot) {
    if (!uploadEnabled || !threadId)
        return;
    const observedAtEpochMs = Date.now();
    const baseDiagnostic = {
        visibility_state: browserVisibilityState(),
        livemark_aggregate_ready: snapshot.isReady,
        registered_adapter_count: Math.min(snapshot.registeredAdapterCount, MAX_DIAGNOSTIC_ADAPTER_COUNT),
        pending_adapter_count: Math.min(snapshot.pendingAdapterCount, MAX_DIAGNOSTIC_ADAPTER_COUNT),
    };
    const cached = { observedAtEpochMs, renderDiagnostic: baseDiagnostic };
    livemarkRenderStates.set(threadId, cached);
    evictOldest(livemarkRenderStates, MAX_RENDERED_THREADS);
    for (const pending of pendingRendersForThread(threadId)) {
        appendCachedDiagnostic(pending, 'RENDER_LIVEMARK_STATE_CHANGED', cached);
    }
}
/**
 * `ThreadScrollProvider.onLivemarkFlush` seam: `isRendered` is true once the
 * thread identified by `threadId` (its context uuid) has reported terminal
 * state and drained mounted Livemark parser/buffer adapters through layout
 * effects. This is callback readiness, not a browser paint observation. Only
 * that thread's pending streams are finalized, and only a
 * flush soon after the terminal event counts under the historical final-render milestone — a thread
 * opened minutes later still uploads, without the milestone. A rendered flush
 * with nothing pending is remembered briefly for the stream whose `completed`
 * event is still in flight. A non-rendered flush is only sent while the thread
 * is streaming (never for a completed thread whose Markdown is still
 * rendering): it forgets that memory and retires attempts still waiting for
 * their render without the milestone, so the next answer's render can only
 * ever finalize its own attempt.
 */
export function notifyAskLifecycleLivemarkFlush(isRendered, threadId) {
    if (!threadId) {
        return;
    }
    const now = Date.now();
    const pendingForThread = takePendingRenders(threadId);
    if (!isRendered) {
        renderedThreadsAt.delete(threadId);
        for (const pending of pendingForThread) {
            finalize(pending.stream, pending.threadId, pending.outcome, pending.terminal, false, pending.renderDiagnostics);
        }
        return;
    }
    for (const pending of pendingForThread) {
        finalize(pending.stream, pending.threadId, pending.outcome, pending.terminal, now - pending.terminalAt <= FINAL_RENDER_MAX_DELAY_MS, pending.renderDiagnostics);
    }
    if (pendingForThread.length === 0) {
        renderedThreadsAt.set(threadId, now);
        evictOldest(renderedThreadsAt, MAX_RENDERED_THREADS);
    }
}
function takePendingRenders(threadId) {
    const taken = [];
    for (const [streamId, pending] of pendingRenders) {
        if (pending.threadId !== threadId) {
            continue;
        }
        clearTimeout(pending.timeoutId);
        pendingRenders.delete(streamId);
        taken.push(pending);
    }
    return taken;
}
async function postLifecycleAttempt(entryUuid, contextUuid, attempt) {
    try {
        const { error, response } = await entryClient.POST('/rest/entry/lifecycle/{entry_uuid}', UPLOAD_REASON, {
            params: { path: { entry_uuid: entryUuid } },
            body: { ...attempt, context_uuid: contextUuid },
            numRetries: 0,
        });
        if (error) {
            logger.info('Ask lifecycle upload skipped', {
                request_id: attempt.request_id,
                status: response.status,
            });
        }
        return response.status;
    }
    catch (error) {
        logger.info('Ask lifecycle upload skipped', { request_id: attempt.request_id, error });
        return undefined;
    }
}
async function uploadAttempt(stream, contextUuid, outcome, terminal, renderDiagnostics) {
    const entryUuid = stream.entryId;
    if (!uploadEnabled || !entryUuid || !contextUuid) {
        return;
    }
    const attempt = buildBrowserLifecycleAttempt(stream, outcome, terminal);
    await new Promise((resolve) => setTimeout(resolve, UPLOAD_DELAY_MS));
    const events = renderDiagnostics.length === 0
        ? attempt.events
        : [...attempt.events, ...renderDiagnostics].sort((a, b) => a.observed_at_epoch_ms - b.observed_at_epoch_ms);
    const status = await postLifecycleAttempt(entryUuid, contextUuid, { ...attempt, events });
    if (status !== HTTP_BAD_REQUEST) {
        return;
    }
    // The upload is validated as one unit, so a tier that predates this change —
    // the worker can start reporting answer candidates before the endpoint knows
    // the milestone — or any later validation mismatch on a diagnostic takes the
    // whole browser lane down with it. Resend only what such a tier accepts.
    const established = events.filter((event) => !ADDED_BROWSER_MILESTONES.has(event.milestone));
    if (established.length === events.length) {
        return;
    }
    await postLifecycleAttempt(entryUuid, contextUuid, { ...attempt, events: established });
}
//# sourceMappingURL=askLifecycle.js.map